import type { ApolloError } from "@apollo/client/errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { type BaseSyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, type SubmitHandler } from "react-hook-form";
import { CalloutIntent, DialogComponent, Modal, TextField, VStack } from "widgets";
import { useFormAria, useOverlay } from "widgets/hooks";
import type z from "zod";
import { array, number, object, string } from "zod";

import type { InputVirtualGenomeInput, Organism } from "@/graphql.ts";
import UserGroupMultiSelect from "@/modules/projects/components/UserGroupMultiSelect.tsx";
import { SelectedGenomesTiles } from "@/modules/workflow/components/parameters/SelectedGenomesTiles.tsx";
import {
  postSelectableUserGroups,
  type UserGroupIntersectionResponse,
} from "@/modules/workflow/rest/virtual-genomes.ts";
import {
  useCheckSelectionContainsNoVirtualGenomes,
  useCheckVirtualGenomeExists,
} from "@/modules/workflow/utils/virtual-genomes.ts";
import { useCreateVirtualGenomeMutation } from "@/ui-modules/entities/genomes/graphql/genomes.gql.ts";

interface CreateVirtualGenomeDialogProps {
  isOpen: boolean;
  onClose: (virtualGenome?: Organism | null) => void;
  selectedGenomes: number[];
}

const virtualGenomeSchema = object({
  name: string({ required_error: "Genome name is required" }).trim().min(1, { message: "Genome name is required" }),
  description: string().trim().optional(),
  visibility: array(number().int().nonnegative()).min(1, { message: "Usergroup selection is required" }),
});

type VirtualGenomeFormData = z.infer<typeof virtualGenomeSchema>;

export const CreateVirtualGenomeDialog = ({ isOpen, onClose, selectedGenomes }: CreateVirtualGenomeDialogProps) => {
  const noGenomesSelected = selectedGenomes.length === 0;
  const [isLoadingUserGroups, setLoadingUserGroups] = useState<boolean>(false);
  const [matchingUsergroups, setMatchingUsergroups] = useState<UserGroupIntersectionResponse | undefined>([]);

  // Dialog
  const { overlayProps, state } = useOverlay({
    isOpen: isOpen,
    onOpenChange(isOpen) {
      if (!isOpen) onClose();
    },
  });

  // Form
  const {
    handleSubmit,
    setError,
    setValue,
    trigger,
    control,
    formState: { errors, isSubmitting, isValid },
  } = useFormAria<VirtualGenomeFormData>({
    defaultValues: { name: "", description: "", visibility: [] },
    resolver: zodResolver(virtualGenomeSchema),
  });

  const typedHandleSubmit = handleSubmit as unknown as (
    onValid: SubmitHandler<VirtualGenomeFormData>,
  ) => (e?: BaseSyntheticEvent) => Promise<void>;

  // Submission
  const [createVirtualGenome] = useCreateVirtualGenomeMutation();

  // Validation
  const {
    loading: isLoadingVirtualGenomeSelected,
    error: virtualGenomeSelectedFetchError,
    selectionContainsVirtualGenome,
    organisms,
  } = useCheckSelectionContainsNoVirtualGenomes(selectedGenomes);

  const {
    existingVirtualGenomes,
    loading,
    error: virtualGenomeQueryError,
  } = useCheckVirtualGenomeExists(selectedGenomes);

  const virtualGenomeExists = existingVirtualGenomes.length > 0;

  // Allows selecting an existing virtual genome in case only a single one exists
  const singleVirtualGenomeExists = useMemo(() => {
    if (virtualGenomeExists) {
      if (existingVirtualGenomes.length === 1) {
        const existingGenome = existingVirtualGenomes[0];
        setValue("name", existingGenome.name!);
        setValue("description", existingGenome.description ?? undefined);
        if (existingGenome.userGroups) {
          const userGroups = (existingGenome?.userGroups ?? [])
            .map((ug) => ug?.id)
            .filter((id): id is number => id != null);
          setValue("visibility", userGroups);
        }
        return "This virtual genome already exists.";
      }
    }
    return undefined;
  }, [existingVirtualGenomes, setValue, virtualGenomeExists]);

  // Custom error messages that disable the submission
  // Note: isValid will always be derived from the validation result of your input registration rules or schema result.
  // We need to handle async errors independently. See https://react-hook-form.com/docs/useform/seterror
  const asyncValidationError = useMemo(() => {
    if (noGenomesSelected) {
      return "A genome selection is required.";
    } else if (virtualGenomeQueryError) {
      return virtualGenomeQueryError;
    } else if (virtualGenomeSelectedFetchError) {
      return virtualGenomeSelectedFetchError;
    } else if (selectionContainsVirtualGenome) {
      return "It is not possible to create a virtual genome from virtual genomes.";
    } else if (existingVirtualGenomes.length > 1) {
      // rare case that should not exist, see SEL10-1235
      return `This virtual genome already exists multiple times: ${existingVirtualGenomes.map((genome) => genome.name).join(", ")}. Please contact your administrator.`;
    }

    return undefined;
  }, [
    existingVirtualGenomes,
    noGenomesSelected,
    selectionContainsVirtualGenome,
    virtualGenomeQueryError,
    virtualGenomeSelectedFetchError,
  ]);

  const onSubmit: SubmitHandler<VirtualGenomeFormData> = useCallback(
    async (data: VirtualGenomeFormData) => {
      if (singleVirtualGenomeExists) {
        return onClose(existingVirtualGenomes[0] as Organism);
      }

      const virtualGenomeInput: InputVirtualGenomeInput = {
        genomeName: data.name,
        genomeDescription: data.description,
        userGroupIds: data.visibility,
        componentOrgIds: selectedGenomes,
      };

      try {
        const result = await createVirtualGenome({ variables: { virtualGenomeInput } });
        onClose(result.data?.createVirtualGenome as Organism);
      } catch (rejected) {
        const errorMessage = (rejected as ApolloError | Error).message;
        setError("root", { message: errorMessage });
      }
    },
    [createVirtualGenome, existingVirtualGenomes, onClose, selectedGenomes, setError, singleVirtualGenomeExists],
  );

  // Fetch and calculate matching user groups
  useEffect(() => {
    const fetchMatchingUsergroups = async () => {
      if (noGenomesSelected) {
        setMatchingUsergroups([]);
        setLoadingUserGroups(false);
        setValue("visibility", []);
        return;
      }
      setLoadingUserGroups(true);
      postSelectableUserGroups({ genomeIds: selectedGenomes })
        .then((commonUserGroups) => {
          if (commonUserGroups.length === 0) {
            setError("visibility", { message: "The genome selection has no user groups in common" });
          }
          setMatchingUsergroups(commonUserGroups);
        })
        .finally(() => setLoadingUserGroups(false));
    };

    void fetchMatchingUsergroups();
  }, [noGenomesSelected, selectedGenomes, setError, setValue]);

  return (
    <Modal state={state} {...overlayProps}>
      <DialogComponent
        title="Create a New Virtual Genome"
        icon="add"
        mainButton={{
          label: singleVirtualGenomeExists ? "Select" : "Create",
          onPress: () => typedHandleSubmit(onSubmit)(),
          isLoading: isSubmitting || loading || isLoadingVirtualGenomeSelected,
          isDisabled: !singleVirtualGenomeExists && (!isValid || !!asyncValidationError),
        }}
        errorCallout={
          singleVirtualGenomeExists
            ? { message: singleVirtualGenomeExists, intent: CalloutIntent.Warning }
            : asyncValidationError
              ? { message: asyncValidationError }
              : errors?.root?.message
                ? { message: errors?.root?.message }
                : undefined
        }
        closeFn={state.close}
        contentStyle={{ width: 600 }}
      >
        <form data-testid="new-virtual-genome" onSubmit={typedHandleSubmit(onSubmit)}>
          <VStack spacing={15}>
            <Controller
              control={control}
              name={"name"}
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Genome name"
                  isRequired
                  isInvalid={!!errors.name}
                  errorMessage={errors.name?.message?.toString()}
                  onChange={onChange}
                  value={value}
                  isDisabled={noGenomesSelected || virtualGenomeExists}
                  onBlur={() => trigger()} // trigger validation
                />
              )}
            />
            <Controller
              control={control}
              name={"description"}
              render={({ field }) => (
                <TextField
                  label="Genome description"
                  isDisabled={noGenomesSelected || virtualGenomeExists}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="visibility"
              rules={{ onChange: () => trigger() }}
              render={({ field: { onChange, onBlur, value } }) => (
                <UserGroupMultiSelect
                  label="Visibility"
                  description="Only the user groups shared by all of the component genomes will be selectable."
                  isRequired
                  onSelectionChange={(userGroupIds: number[]) => {
                    onChange(userGroupIds);
                    onBlur();
                  }}
                  selectedUserGroups={value}
                  isInvalid={!!errors.visibility}
                  errorMessage={errors.visibility?.message?.toString()}
                  userGroupOptions={matchingUsergroups}
                  isDisabled={isLoadingUserGroups || noGenomesSelected || virtualGenomeExists}
                />
              )}
            />
            <SelectedGenomesTiles organisms={organisms} isLoading={isLoadingVirtualGenomeSelected} />
          </VStack>
        </form>
      </DialogComponent>
    </Modal>
  );
};
